import React, { useEffect, useState, useCallback } from 'react'
import { useDebouncedEffect } from '@/hooks/useDebouncedEffect'
import { useNavigate } from '@tanstack/react-router'
import { useSetAtom, useAtomValue } from 'jotai'
import { apis } from '@modules/apiClients'
import { UserService } from '@duck/api-definitions'
import { setUserAtom, accessTokenAtom, isLoginDialogOpenAtom } from '@/store/userAtoms'
import { toast } from 'sonner'
import { ResendVerificationForm } from '../ResendVerificationForm'
import { StandardLoader } from '../StandardLoader'
import { useTar } from '@/modules'
import { Button, Card, CardContent, CardHeader, CardTitle } from '@duck/ui'
import { identifyUserInSentry } from '@/instrument.sentry'

const STATUS_COLORS: Record<'loading' | 'success' | 'error' | 'resending' | 'alreadyVerified', string> = {
  success: 'text-success',
  alreadyVerified: 'text-success',
  error: 'text-error',
  resending: 'text-accent',
  loading: 'text-muted-foreground'
}

const getStatusColor = (status: 'loading' | 'success' | 'error' | 'resending' | 'alreadyVerified') =>
  STATUS_COLORS[status] ?? 'text-muted-foreground'

interface VerificationState {
  status: 'loading' | 'success' | 'error' | 'resending' | 'alreadyVerified'
  message: string
}

interface VerifyEmailProps {
  token: string
}

const EMAIL_ALREADY_VERIFIED = 'Email is already verified'

export const VerifyEmail = ({ token }: VerifyEmailProps) => {
  const navigate = useNavigate()
  const { t } = useTar()
  const setUser = useSetAtom(setUserAtom)
  const setAccessToken = useSetAtom(accessTokenAtom)
  const accessToken = useAtomValue(accessTokenAtom)
  const setIsLoginDialogOpen = useSetAtom(isLoginDialogOpenAtom)
  const [state, setState] = useState<VerificationState>(() => {
    return {
      status: 'loading',
      message: t('verification.verifying')
    }
  })
  const [showResendForm, setShowResendForm] = useState(false)

  const debouncedVerifyEmail = useCallback(async (tokenToVerify: string) => {

    setState({
      status: 'loading',
      message: t('verification.verifying')
    })

    try {
      const verifyRequest: UserService.VerifyEmailRequest = { token: tokenToVerify }
      const { data } = await apis.userService.authVerifyEmailPost(verifyRequest)

      setState({
        status: 'success',
        message: t('verification.success')
      })
      setUser(data.user)
      setAccessToken(data.accessToken)
      identifyUserInSentry(data.user)
      if (data.person?.ongoingAssignments?.length && data.person.ongoingAssignments.length > 0) {
        navigate({ to: '/assignments' })
      } else {
        navigate({ to: '/challenges/available' })
      }

      toast.success(t('verification.successToast'))
    } catch (error: unknown) {
      const errorMessage = (error as { response?: { data?: { error?: string } } })?.response?.data?.error || t('verification.verificationFailed')
      const isAlreadyVerified = errorMessage === EMAIL_ALREADY_VERIFIED

      if (isAlreadyVerified) {
        setState({
          status: 'alreadyVerified',
          message: t('verification.alreadyVerified')
        })
        toast.success(t('verification.alreadyVerified'))
      } else {
        setState({
          status: 'error',
          message: errorMessage
        })
        toast.error(t('verification.verificationFailed'))
      }
    }
  }, [t, setUser, setAccessToken, navigate])

  useDebouncedEffect(
    () => {
      if (!token) {
        setState({
          status: 'error',
          message: t('verification.noToken')
        })
        return
      }
      debouncedVerifyEmail(token)
    },
    [token, debouncedVerifyEmail, t],
    1500
  )

  useEffect(() => {
    if (state.status === 'alreadyVerified' && accessToken) {
      navigate({ to: '/' })
    }
  }, [state.status, accessToken, navigate])

  const handleResendVerification = () => {
    setShowResendForm(true)
  }

  const handleResendSuccess = () => {
    setShowResendForm(false)

    setTimeout(() => {
      navigate({ to: '/' })
    }, 3000)
  }

  const handleResendCancel = () => {
    setShowResendForm(false)
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md min-w-80 ">

        <Card>
          <CardHeader className="text-center">
            <CardTitle>{t('verification.title')}</CardTitle>
          </CardHeader>

          <CardContent className="space-y-6">
            {(state.status === 'loading' || state.status === 'resending') && (
              <div className="flex justify-center">
                <StandardLoader />
              </div>
            )}

            <p className={`text-lg text-center ${getStatusColor(state.status)}`}>
              {state.message}
            </p>

            {state.status === 'success' && (
              <div className="bg-success/10 border border-success/20 rounded-lg p-4">
                <p className="text-sm text-center text-success">
                  {t('verification.redirecting')}
                </p>
              </div>
            )}

            {state.status === 'alreadyVerified' && (
              <div className="space-y-3">
                {accessToken ? (
                  <div className="bg-success/10 border border-success/20 rounded-lg p-4">
                    <p className="text-sm text-center text-success">
                      {t('verification.redirecting')}
                    </p>
                  </div>
                ) : (
                  <>
                    <Button
                      onClick={() => setIsLoginDialogOpen(true)}
                      className="w-full"
                    >
                      {t('common.login')}
                    </Button>
                    <Button
                      onClick={() => navigate({ to: '/' })}
                      variant="outline"
                      className="w-full"
                    >
                      {t('common.goHome')}
                    </Button>
                  </>
                )}
              </div>
            )}

            {state.status === 'error' && (
              <div className="space-y-3">

                {!showResendForm ? (
                  <Button
                    onClick={handleResendVerification}
                    variant="secondary"
                    className="w-full"
                  >
                    {t('verification.requestNewVerificationLink')}
                  </Button>
                ) : (
                  <ResendVerificationForm
                    onSuccess={handleResendSuccess}
                    onCancel={handleResendCancel}
                  />
                )}
              </div>
            )}
            {state.status !== 'success' && state.status !== 'alreadyVerified' && <Button
              onClick={() => navigate({ to: '/' })}
              variant="outline"
              className="w-full"
            >
              {t('common.goHome')}
            </Button>}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
